import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  List,
  ListItem,
  Spinner,
  Text
} from '@chakra-ui/react';
import { Badge, ButtonText, OutlineCard } from '@morressier/alembic';
import AffiliationNotFound from './AffilationNotFound';
import {
  IAffiliationDetails,
  IAuthorJournalInfo,
  IBoardMember,
  IReviewerActivity,
  IUser
} from '@publishing-engine/domain/types/AuthorType';
import { ReactElement, type ReactNode, useRef, useState } from 'react';
import { fetchReviewerActivity, fetchSubmissionContext } from 'src/api/queries';
import { useQuery } from '@tanstack/react-query';
import ReviewerActivityNotFound from './ReviewerActivityNotFound';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinus, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { ReviewerStatsTable, ReviewerStatsRow } from './ReviewerStatsTable';
import { AffiliationDetail } from 'src/components/User/UpdateAffiliationDetails';
import {
  IAuthorWithEmail,
  ILexiconField
} from '@publishing-engine/domain/models';
import JournalSpecificKeywordsSection from './AuthorProfileModal/JournalSpecificKeywordsSection';
import ProfileNotesSection from './AuthorProfileModal/ProfileNotesSection';
import { JournalList } from './AuthorProfileModal/SubmittedAuthorDetailsModal';
import UserEmail from 'src/components/User/UserEmail';
const GlobalMetricsKeys = [
  'TimesUninvited',
  'TimesSelected',
  'DaysSinceLastReview',
  'LastInvitedDate',
  'PercentAcceptedNoReview',
  'PercentInvitedNoReview',
  'ReviewerOnboardedDate',
  'AcceptedNoReview',
  'Agreed',
  'ReviewerPersonDesignation',
  'TotalOpenInvites',
  'LastSubmittedDate',
  'AverageRScore',
  'ActiveReviews',
  'NumberOfTimesSubmitted',
  'AverageReviewerTurnaroundTime',
  'ReviewsInvited',
  'ReviewsNoReply',
  'ReviewsDeclined'
];
type ReviewerActivityValue = string | number | boolean | null | undefined;
type ReviewerActivityField = {
  predicateLabel: string;
  value: ReviewerActivityValue;
  tip?: string | null;
};
type ReviewerActivityRecord = Record<string, ReviewerActivityField | undefined>;

interface ISummaryProps {
  data: {
    affiliationDetails?: IAffiliationDetails;
    additionalAffiliations?: IAffiliationDetails[];
    boardMemberships?: IBoardMember[];
    userId?: string;
    journalName?: string;
    reviewerJournals?: IAuthorJournalInfo[];
  };
  isProfileView?: boolean;
  showReviewerActivity?: boolean;
  showReviewerActivityForEachJournal?: boolean;
  reviewerConfig?: ILexiconField[];
  journals?: JournalList;
  submissionContextId?: string;
  organizationId?: string;
  user?: IUser;
  profileUser?: IUser | IAuthorWithEmail | undefined;
  isDeactivatedAccount?: boolean;
}

const normalizeEmails = (emails?: string[]) =>
  Array.from(
    new Map(
      (emails ?? [])
        .map(email => email.trim())
        .filter(Boolean)
        .map(email => [email.toLowerCase(), email] as const)
    ).values()
  );

const useReviewerCardConfig = (reviewerConfig?: ILexiconField[]) => {
  const getConfig = (predicateLabel: string) =>
    reviewerConfig?.find(f => f.predicateLabel === predicateLabel);

  return { getConfig };
};

const SUMMARY_SECTION_SCROLL_OFFSET = 120;

const SummarySectionLink = ({
  label,
  count,
  onClick
}: {
  label: string;
  count: string;
  onClick: () => void;
}) => (
  <Flex align="center" gap={1} wrap="wrap">
    <Text textStyle="textBody">{`${label}:`}</Text>
    <ButtonText onClick={onClick} isDisabled={count === '0'} minW={0}>
      {count}
    </ButtonText>
  </Flex>
);

const SummaryAccordionItem = ({
  title,
  count,
  sectionRef,
  children
}: {
  title: string;
  count?: string | number;
  sectionRef?: (node: HTMLDivElement | null) => void;
  children: ReactNode;
}) => (
  <Box ref={sectionRef} scrollMarginTop={`${SUMMARY_SECTION_SCROLL_OFFSET}px`}>
    <AccordionItem
      mb={3}
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="md"
      overflow="hidden"
      bg="white"
    >
      {({ isExpanded }) => (
        <>
          <AccordionButton px={4} py={3} bgColor="surfaceSecondaryFill">
            <Flex
              gap={2}
              textAlign="left"
              alignItems="center"
              flex="1"
              minWidth={0}
            >
              <Text textStyle="headingGroup" fontSize="md" noOfLines={1}>
                {title}
              </Text>
              {count !== undefined && (
                <Badge size="context" variant="neutral">
                  {count}
                </Badge>
              )}
            </Flex>
            <Box ml={2} flexShrink={0}>
              {isExpanded ? (
                <FontAwesomeIcon icon={faMinus} />
              ) : (
                <FontAwesomeIcon icon={faPlus} />
              )}
            </Box>
          </AccordionButton>
          <AccordionPanel px={4} pb={4} pt={3}>
            {children}
          </AccordionPanel>
        </>
      )}
    </AccordionItem>
  </Box>
);

function formatReviewerActivityValue(
  value: ReviewerActivityValue
): string | number {
  if (value === undefined || value === null || value === '') {
    return '-';
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  return value;
}

function formatRows(
  reviewerActivity: ReviewerActivityRecord,
  globalReviewerActivity: ReviewerActivityRecord,
  getConfig: (key: string) => ILexiconField | undefined
): ReviewerStatsRow[] {
  return GlobalMetricsKeys.map<ReviewerStatsRow | null>(key => {
    const config = getConfig(key);
    if (!config || config?.reviewerCard !== true) return null;
    const reviewerField = reviewerActivity[key];
    const globalField = globalReviewerActivity[key];
    return {
      label: config?.label,
      labelKey: key,
      value: formatReviewerActivityValue(reviewerField?.value),
      globalValue: formatReviewerActivityValue(globalField?.value),
      tip: config?.tooltip || globalField?.tip || null
    };
  }).filter((row): row is ReviewerStatsRow => row !== null);
}

function mapReviewerStats(
  data: IReviewerActivity | undefined,
  reviewerConfig?: ILexiconField[]
): ReviewerStatsRow[] | undefined {
  const { getConfig } = useReviewerCardConfig(reviewerConfig);
  const reviewerActivity: ReviewerActivityRecord = {};
  const globalReviewerActivity: ReviewerActivityRecord = {};
  for (const field of data?.reviewer ?? []) {
    reviewerActivity[field.predicateLabel] = field;
  }
  for (const field of data?.globalReviewer ?? []) {
    globalReviewerActivity[field.predicateLabel] = field;
  }

  if (
    !data ||
    (data?.reviewer?.length === 0 && data?.globalReviewer?.length === 0)
  ) {
    return undefined;
  }

  return formatRows(reviewerActivity, globalReviewerActivity, getConfig);
}
export const ReviewerActivity = ({
  userId,
  submissionContextId,
  journalName,
  reviewerConfig
}: {
  userId: string;
  submissionContextId: string;
  journalName?: string;
  reviewerConfig?: ILexiconField[];
}) => {
  const { data: submissionContext, isLoading: isSubmissionContextLoading } =
    useQuery({
      queryKey: ['submission-context', submissionContextId],
      queryFn: () =>
        !reviewerConfig && submissionContextId
          ? fetchSubmissionContext(submissionContextId)
          : null,
      enabled: !reviewerConfig && Boolean(submissionContextId)
    });
  const globalLexiconConfig = submissionContext?.config?.globalLexiconConfig;
  const { data: ReviewerActivity, isLoading } = useQuery({
    queryKey: ['reviewer-activity', { submissionContextId, userId }],
    queryFn: () => fetchReviewerActivity(submissionContextId, userId)
  });

  const rows = mapReviewerStats(
    ReviewerActivity,
    reviewerConfig ?? submissionContext?.config?.cms?.lexicon?.fields
  );
  const isConfigLoading = !reviewerConfig && isSubmissionContextLoading;

  return (
    <>
      <Flex direction="column" width="100%" gap={3}>
        <Flex alignItems={'flex-start'} gap={2} flexWrap="wrap">
          <Text>{`${journalName}`} </Text>
        </Flex>

        {isLoading || isConfigLoading ? (
          <Spinner></Spinner>
        ) : rows ? (
          <ReviewerStatsTable
            globalLexiconConfig={globalLexiconConfig}
            rows={rows}
          />
        ) : (
          <ReviewerActivityNotFound />
        )}
      </Flex>
    </>
  );
};

export const AffiliationDetails = ({
  affiliationDetails,
  additionalAffiliations,
  buttonRight
}: {
  affiliationDetails: IAffiliationDetails | undefined;
  additionalAffiliations?: IAffiliationDetails[] | undefined;
  isProfileView?: boolean;
  buttonRight?: ReactElement;
}) => {
  return (
    <Flex direction="column" width="100%" gap={3}>
      <Flex
        justifyContent={'space-between'}
        flexWrap="wrap"
        gap={2}
        alignItems="flex-start"
      >
        <Text textStyle="headingItem">Primary Affiliation</Text>
        {buttonRight && buttonRight}
      </Flex>
      {affiliationDetails?.organizationName ? (
        <AffiliationDetail affiliation={affiliationDetails} />
      ) : (
        <AffiliationNotFound />
      )}
      {additionalAffiliations?.length !== 0 && (
        <Flex direction="column" width="100%" gap={3}>
          <Flex
            justifyContent={'space-between'}
            flexWrap="wrap"
            gap={2}
            alignItems="flex-start"
          >
            <Text textStyle="headingItem">Secondary Affiliations</Text>
            {buttonRight && buttonRight}
          </Flex>
          {additionalAffiliations?.map(affiliation => (
            <AffiliationDetail affiliation={affiliation} />
          ))}
        </Flex>
      )}
    </Flex>
  );
};

export const BoardMemberDetails = ({
  boardMemberships
}: {
  boardMemberships: IBoardMember[];
}) =>
  boardMemberships && boardMemberships?.length ? (
    <Flex direction="column" width="100%" gap={3}>
      <Text textStyle="headingGroup">Board Memberships</Text>
      <Flex direction={'column'} gap={3}>
        {boardMemberships?.map(data => (
          <OutlineCard borderless width="100%" padding={4} key={data.id}>
            <Badge size="context" marginBottom={2}>
              Board Member
            </Badge>
            <Text textStyle="headingItem">{data.name}</Text>
          </OutlineCard>
        ))}
      </Flex>
    </Flex>
  ) : null;

export const AdditionalEmailDetails = ({
  additionalEmails
}: {
  additionalEmails?: string[];
}) => {
  const normalizedEmails = normalizeEmails(additionalEmails);

  return normalizedEmails.length ? (
    <List spacing={1} styleType="disc" stylePosition="inside">
      {normalizedEmails.map(email => (
        <ListItem key={email}>{email}</ListItem>
      ))}
    </List>
  ) : null;
};

export const EmailDetails = ({
  user,
  isDeactivatedAccount
}: {
  user?: IUser;
  isDeactivatedAccount?: boolean;
}) => {
  const normalizedAdditionalEmails = normalizeEmails(user?.additionalEmails);

  return (
    <Flex direction="column" width="100%" gap={3}>
      {user?.email && (
        <UserEmail
          emailType="Primary Email"
          timeStamp={user.lastSentEmailTimeStamp}
          lastSentStatus={user.lastSentEmailStatus}
          email={user.email}
          enableIcon={Object.hasOwn(user, 'isEmailDelivered')}
          isEmailDelivered={user.isEmailDelivered}
          isUserDeactivated={isDeactivatedAccount || false}
        />
      )}
      {user?.secondaryEmail?.trim() && (
        <UserEmail
          emailType="Secondary Email"
          timeStamp={user.secondaryEmailDetails?.emailTimeStamp}
          lastSentStatus={''}
          email={user.secondaryEmail}
          enableIcon={Object.hasOwn(user, 'isEmailDelivered')}
          isEmailDelivered={user.secondaryEmailDetails?.isEmailDelivered}
          isUserDeactivated={isDeactivatedAccount || false}
        />
      )}

      {user?.ccEmail?.trim() && (
        <UserEmail
          emailType="CC Email"
          timeStamp={user.ccEmailDetails?.emailTimeStamp}
          lastSentStatus={''}
          email={user.ccEmail}
          enableIcon={Object.hasOwn(user, 'isEmailDelivered')}
          isEmailDelivered={user.ccEmailDetails?.isEmailDelivered}
          isUserDeactivated={isDeactivatedAccount || false}
        />
      )}

      {normalizedAdditionalEmails.length !== 0 && (
        <Flex direction="column" gap={1}>
          <Text textStyle="headingItem">Additional Emails</Text>
          <AdditionalEmailDetails
            additionalEmails={normalizedAdditionalEmails}
          />
        </Flex>
      )}
    </Flex>
  );
};

const ReviewerJournalAccordion = ({
  journal,
  userId,
  reviewerConfig
}: {
  journal: IAuthorJournalInfo;
  userId: string;
  reviewerConfig?: ILexiconField[];
}) => {
  const [hasExpanded, setHasExpanded] = useState(false);
  const { data: submissionContext, isLoading: isSubmissionContextLoading } =
    useQuery({
      queryKey: ['submission-context', journal.id],
      queryFn: () => (journal?.id ? fetchSubmissionContext(journal.id) : null),
      enabled: Boolean(journal?.id) && hasExpanded
    });
  const globalLexiconConfig = submissionContext?.config?.globalLexiconConfig;
  const { data: ReviewerActivity, isLoading } = useQuery({
    queryKey: [
      'reviewer-activity',
      { submissionContextId: journal.id, userId }
    ],
    queryFn: () => fetchReviewerActivity(journal.id, userId),
    enabled: !!userId && hasExpanded
  });

  const rows = mapReviewerStats(
    ReviewerActivity,
    reviewerConfig ?? submissionContext?.config?.cms?.lexicon?.fields
  );
  const isConfigLoading =
    !reviewerConfig && hasExpanded && isSubmissionContextLoading;

  return (
    <AccordionItem
      key={journal.id}
      border="1px solid"
      borderColor="gray.200"
      borderRadius="4px"
      my={2}
    >
      {({ isExpanded }) => (
        <>
          <AccordionButton
            bgColor="surfaceSecondaryFill"
            padding={3}
            onClick={() => {
              if (!hasExpanded) {
                setHasExpanded(true);
              }
            }}
          >
            <Flex
              gap={2}
              textAlign="left"
              alignItems="center"
              flex="1"
              minWidth={0}
            >
              <Text textStyle="textBody" noOfLines={2}>
                {journal.name}
              </Text>
            </Flex>
            <Box ml={2} flexShrink={0}>
              {isExpanded ? (
                <FontAwesomeIcon icon={faMinus} />
              ) : (
                <FontAwesomeIcon icon={faPlus} />
              )}
            </Box>
          </AccordionButton>
          <AccordionPanel
            padding={0}
            borderTopWidth="0.5px"
            borderTopStyle="solid"
            borderTopColor="gray.300"
          >
            <Box>
              {isLoading || isConfigLoading ? (
                <Flex justifyContent="center" padding={4}>
                  <Spinner />
                </Flex>
              ) : rows ? (
                <ReviewerStatsTable
                  globalLexiconConfig={globalLexiconConfig}
                  rows={rows}
                />
              ) : (
                <ReviewerActivityNotFound />
              )}
            </Box>
          </AccordionPanel>
        </>
      )}
    </AccordionItem>
  );
};

export const ReviewerJournalsDetails = ({
  reviewerJournals,
  userId,
  reviewerConfig
}: {
  reviewerJournals: IAuthorJournalInfo[];
  userId: string;
  reviewerConfig?: ILexiconField[];
}) =>
  reviewerJournals && reviewerJournals?.length ? (
    <Flex direction="column" width="100%" gap={3}>
      <Accordion allowToggle>
        {reviewerJournals?.map(journal => (
          <ReviewerJournalAccordion
            key={journal.id}
            journal={journal}
            userId={userId}
            reviewerConfig={reviewerConfig}
          />
        ))}
      </Accordion>
    </Flex>
  ) : null;

const SummaryTab = ({
  data,
  isProfileView = false,
  journals,
  submissionContextId,
  organizationId,
  user,
  profileUser,
  isDeactivatedAccount,
  reviewerConfig
}: ISummaryProps) => {
  const {
    affiliationDetails,
    additionalAffiliations,
    boardMemberships,
    userId,
    reviewerJournals
  } = data;
  const reviewerActivityOffset = 1;
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [notesCount, setNotesCount] = useState(0);
  const journalSpecificCount = journals?.length ?? 0;
  const reviewerJournalCount = reviewerJournals?.length ?? 0;
  const affiliationCount = affiliationDetails?.organizationName ? 1 : 0;
  const additionalAffiliationCount = additionalAffiliations?.length ?? 0;
  const emailCount =
    Number(Boolean(user?.email?.trim())) +
    Number(Boolean(user?.secondaryEmail?.trim())) +
    Number(Boolean(user?.ccEmail?.trim())) +
    normalizeEmails(user?.additionalEmails).length;
  const boardMembershipCount = boardMemberships?.length ?? 0;
  const isInvitedUser = user?.status === 'invited';
  const sectionIdToIndex: Record<string, number> = {};
  let idx = 0;
  sectionIdToIndex['email-section'] = idx++;
  sectionIdToIndex['affiliations-section'] = idx++;
  sectionIdToIndex['reviewer-activity-section'] = idx++;
  if (!isInvitedUser) {
    sectionIdToIndex['journal-specific-section'] = idx++;
  }
  if (!isInvitedUser) {
    sectionIdToIndex['notes-section'] = idx++;
  }
  if (boardMembershipCount > 0) {
    sectionIdToIndex['board-memberships-section'] = idx++;
  }
  const scrollToSection = (sectionId: string) => {
    sectionRefs.current[sectionId]?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
    const accIdx = sectionIdToIndex[sectionId];
    if (accIdx !== undefined) {
      setAccordionIndex(prev =>
        prev.includes(accIdx) ? prev : [...prev, accIdx]
      );
    }
  };
  const [accordionIndex, setAccordionIndex] = useState<number[]>(() => {
    const indices: number[] = [];

    if (isInvitedUser) {
      indices.push(0, 1, 2);

      if (boardMembershipCount > 0) {
        indices.push(2 + reviewerActivityOffset);
      }
    } else {
      indices.push(0, 1, 2);

      indices.push(2 + reviewerActivityOffset, 3 + reviewerActivityOffset);

      if (boardMembershipCount > 0) {
        indices.push(4 + reviewerActivityOffset);
      }
    }

    return indices;
  });

  const summarySections = [
    {
      label: 'Email(s)',
      count: String(emailCount),
      onClick: () => scrollToSection('email-section')
    },
    {
      label: 'Affiliations',
      count: String(affiliationCount + additionalAffiliationCount),
      onClick: () => scrollToSection('affiliations-section')
    },
    {
      label: 'Reviewer Activity',
      count: `${reviewerJournalCount} Journal${
        reviewerJournalCount !== 1 ? 's' : ''
      }`,
      onClick: () => scrollToSection('reviewer-activity-section')
    },
    !isInvitedUser
      ? {
          label: 'Journal Specific Specialty & Keyword(s)',
          count: `${journalSpecificCount} Journal${
            journalSpecificCount !== 1 ? 's' : ''
          }`,
          onClick: () => scrollToSection('journal-specific-section')
        }
      : null,
    {
      label: 'Notes',
      count: String(notesCount),
      onClick: () => scrollToSection('notes-section')
    },
    boardMembershipCount > 0
      ? {
          label: 'Board Memberships',
          count: String(boardMembershipCount),
          onClick: () => scrollToSection('board-memberships-section')
        }
      : null
  ].filter(
    (
      section
    ): section is {
      label: string;
      count: string;
      onClick: () => void;
    } => section !== null
  );

  return (
    <Flex direction="column" width="100%" gap={3}>
      {summarySections.length > 0 && (
        <Flex gap={2} wrap="wrap" align="center">
          {summarySections.map((section, index) => (
            <Flex key={section.label} align="center" gap={2} wrap="wrap">
              <SummarySectionLink
                label={section.label}
                count={section.count}
                onClick={section.onClick}
              />
              {index !== summarySections.length - 1 && <Text>|</Text>}
            </Flex>
          ))}
        </Flex>
      )}

      <Accordion
        allowMultiple
        allowToggle
        index={accordionIndex}
        onChange={(expanded: number | number[]) =>
          setAccordionIndex(Array.isArray(expanded) ? expanded : [expanded])
        }
        width="100%"
        marginBottom={3}
      >
        <SummaryAccordionItem
          title="Email(s)"
          count={emailCount}
          sectionRef={node => {
            sectionRefs.current['email-section'] = node;
          }}
        >
          <EmailDetails
            user={profileUser}
            isDeactivatedAccount={isDeactivatedAccount}
          />
        </SummaryAccordionItem>
        <SummaryAccordionItem
          title="Affiliations"
          count={affiliationCount + additionalAffiliationCount}
          sectionRef={node => {
            sectionRefs.current['affiliations-section'] = node;
          }}
        >
          <AffiliationDetails
            affiliationDetails={affiliationDetails}
            isProfileView={isProfileView}
            additionalAffiliations={additionalAffiliations}
          />
        </SummaryAccordionItem>
        <SummaryAccordionItem
          title="Reviewer Activity"
          count={`${reviewerJournalCount} Journal${
            reviewerJournalCount !== 1 ? 's' : ''
          }`}
          sectionRef={node => {
            sectionRefs.current['reviewer-activity-section'] = node;
          }}
        >
          <Flex direction="column" gap={4}>
            <ReviewerJournalsDetails
              reviewerJournals={reviewerJournals ?? []}
              userId={userId ?? ''}
              reviewerConfig={reviewerConfig}
            />
          </Flex>
        </SummaryAccordionItem>
        {!isInvitedUser && (
          <SummaryAccordionItem
            title="Journal Specific Specialty & Keyword(s)"
            count={`${journalSpecificCount} Journal${
              journalSpecificCount !== 1 ? 's' : ''
            }`}
            sectionRef={node => {
              sectionRefs.current['journal-specific-section'] = node;
            }}
          >
            <JournalSpecificKeywordsSection
              userId={userId ?? ''}
              journals={journals}
            />
          </SummaryAccordionItem>
        )}

        <SummaryAccordionItem
          title="Notes"
          count={notesCount}
          sectionRef={node => {
            sectionRefs.current['notes-section'] = node;
          }}
        >
          <ProfileNotesSection
            userId={userId}
            submissionContextId={submissionContextId}
            organizationId={organizationId}
            onTotalChange={setNotesCount}
            canEditNotes={!isInvitedUser}
          />
        </SummaryAccordionItem>
        {boardMembershipCount > 0 && (
          <SummaryAccordionItem
            title="Board Memberships"
            count={boardMembershipCount}
            sectionRef={node => {
              sectionRefs.current['board-memberships-section'] = node;
            }}
          >
            <BoardMemberDetails boardMemberships={boardMemberships || []} />
          </SummaryAccordionItem>
        )}
      </Accordion>
    </Flex>
  );
};

export default SummaryTab;
